import React from 'react';
import { Box, Typography, Grid, Container, Chip } from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SettingsIcon from '@mui/icons-material/Settings';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import BarChartIcon from '@mui/icons-material/BarChart';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const modules = [
  {
    icon: PersonAddIcon,
    color: '#2563eb', bg: '#eff6ff',
    title: 'Admission & Enrollment',
    points: ['Online admission form', 'Document checklist', 'Fee receipt on admission', 'Class assignment & roll no.'],
  },
  {
    icon: AccountBalanceWalletIcon,
    color: '#0891b2', bg: '#e0f2fe',
    title: 'Fee & Finance Management',
    points: ['Fee structure by class', 'Online payment gateway', 'Automated due reminders', 'Concession & scholarship mgmt'],
  },
  {
    icon: SettingsIcon,
    color: '#7c3aed', bg: '#ede9fe',
    title: 'Staff & HR Module',
    points: ['Staff profile management', 'Leave & attendance tracking', 'Payroll computation', 'PF/ESI statutory reports'],
  },
  {
    icon: BarChartIcon,
    color: '#059669', bg: '#d1fae5',
    title: 'Parent Communication',
    points: ['Daily homework alerts', 'Exam results sharing', 'Event & notice circulars', 'Parent-teacher meeting scheduling'],
  },
  {
    icon: EmojiEventsIcon,
    color: '#dc2626', bg: '#fee2e2',
    title: 'Library Management',
    points: ['Book catalog with ISBN', 'Issue & return tracking', 'Overdue fine alerts', 'Student borrowing history'],
  },
  {
    icon: DirectionsBusIcon,
    color: '#d97706', bg: '#fef3c7',
    title: 'Transport Management',
    points: ['Route & stop management', 'Student bus assignment', 'Driver contact info', 'Attendance by bus route'],
  },
];

const ModuleCards = () => (
  <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#f8fafc' }}>
    <Container maxWidth="lg">
      <Box sx={{ textAlign: 'center', mb: 8 }}>
        <Chip label="ALL MODULES" size="small" sx={{ bgcolor: '#eff6ff', color: '#2563eb', fontWeight: 800, letterSpacing: 1.5, fontSize: 11, fontFamily: "'Montserrat', sans-serif", mb: 2 }} />
        <Typography variant="h2" sx={{ fontWeight: 900, color: '#0f172a', fontFamily: "'Montserrat', sans-serif", fontSize: { xs: '2rem', md: '2.8rem' } }}>
          6 Powerful Modules, One Platform
        </Typography>
        <Typography sx={{ color: '#64748b', fontSize: 17, mt: 2, fontFamily: "'Montserrat', sans-serif", maxWidth: 560, mx: 'auto', lineHeight: 1.7 }}>
          Every module is tightly integrated so data flows seamlessly across your school.
        </Typography>
      </Box>

      {/* Cards — equal height via flex + height 100% */}
      <Grid container spacing={3} alignItems="stretch">
        {modules.map((m, i) => {
          const IconComp = m.icon;
          return (
            <Grid item xs={12} sm={6} md={4} key={i} sx={{ display: 'flex' }}>
              <Box sx={{
                width: '100%',
                bgcolor: '#ffffff',
                borderRadius: '20px',
                p: 3.5,
                borderTop: `4px solid ${m.color}`,
                boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                border: '1px solid #f1f5f9',
                borderTopColor: m.color,
                transition: 'all 0.3s',
                display: 'flex', flexDirection: 'column',
                '&:hover': {
                  transform: 'translateY(-7px)',
                  boxShadow: `0 20px 50px ${m.color}18`,
                },
              }}>
                {/* Icon + title row */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box sx={{ width: 52, height: 52, borderRadius: '14px', bgcolor: m.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <IconComp sx={{ fontSize: 26, color: m.color }} />
                  </Box>
                  <Typography sx={{ fontWeight: 800, color: '#0f172a', fontFamily: "'Montserrat', sans-serif", fontSize: '0.97rem', lineHeight: 1.3 }}>{m.title}</Typography>
                </Box>

                {/* Feature bullet points */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1, flex: 1 }}>
                  {m.points.map((p, j) => (
                    <Box key={j} sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: m.color, flexShrink: 0 }} />
                      <Typography sx={{ color: '#475569', fontSize: 13.5, fontFamily: "'Montserrat', sans-serif", lineHeight: 1.5 }}>{p}</Typography>
                    </Box>
                  ))}
                </Box>

                {/* "Learn more" link at bottom — always aligned */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 3, cursor: 'pointer' }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: m.color, fontFamily: "'Montserrat', sans-serif" }}>Explore module</Typography>
                  <ArrowForwardIcon sx={{ fontSize: 14, color: m.color }} />
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Container>
  </Box>
);

export default ModuleCards;
